import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase/client";
import { getAppointmentDisplayNotes } from "../utils/googleCalendarText";

// ─── Types ───────────────────────────────────────────────────────────────────

export type AppointmentType =
  | "Consulta"
  | "Cirurgia"
  | "Follow-up"
  | "Pré-op"
  | "Retorno"
  | "Urgência"
  | "Outro";

export type EventColor = "blue" | "green" | "purple" | "amber" | "red";
export type AppointmentOrigin = "orthoprecision" | "google";

export interface Appointment {
  id: string;
  patientId: string | null;
  patientName: string;
  patientDiagnosis?: string;
  patientAlerts?: string[];
  type: AppointmentType;
  startDate: number; // Unix ms
  endDate: number;   // Unix ms
  location: string;
  notes: string;
  description?: string;
  rawGoogleDescription?: string;
  color: EventColor;
  googleEventId?: string;
  googleCalendarId?: string;
  origin: AppointmentOrigin;
  notifyPatient: boolean;
  syncGoogle: boolean;
  pendingGoogleSync?: boolean;
  doctorUid: string;
  createdAt?: number;
  updatedAt?: number;
}

export type AppointmentInput = Omit<Appointment, "id" | "doctorUid" | "createdAt" | "updatedAt">;

// ─── Color map ────────────────────────────────────────────────────────────────

export const EVENT_COLORS: Record<EventColor, { bg: string; border: string; text: string; dot: string }> = {
  blue:   { bg: "bg-cyan-500/20",   border: "border-cyan-500/50",   text: "text-cyan-300",   dot: "bg-cyan-400" },
  green:  { bg: "bg-emerald-500/20",border: "border-emerald-500/50",text: "text-emerald-300", dot: "bg-emerald-400" },
  purple: { bg: "bg-violet-500/20", border: "border-violet-500/50", text: "text-violet-300",  dot: "bg-violet-400" },
  amber:  { bg: "bg-amber-500/20",  border: "border-amber-500/50",  text: "text-amber-300",   dot: "bg-amber-400" },
  red:    { bg: "bg-red-500/20",    border: "border-red-500/50",    text: "text-red-300",     dot: "bg-red-400" },
};

export const DEFAULT_EVENT_COLOR: EventColor = "blue";

/**
 * Firestore documents (legacy records, manual edits, imperfect Google Calendar
 * syncs) may carry a `color` that is missing or outside the EventColor enum.
 * Always resolve through this helper instead of indexing EVENT_COLORS directly.
 */
export function getEventColorStyle(color: unknown): { bg: string; border: string; text: string; dot: string } {
  if (typeof color === "string" && color in EVENT_COLORS) {
    return EVENT_COLORS[color as EventColor];
  }
  return EVENT_COLORS[DEFAULT_EVENT_COLOR];
}

/** Normalizes a raw value into a valid EventColor, falling back to the default. */
export function normalizeEventColor(color: unknown): EventColor {
  return typeof color === "string" && color in EVENT_COLORS ? (color as EventColor) : DEFAULT_EVENT_COLOR;
}

export const TYPE_DEFAULT_COLOR: Record<AppointmentType, EventColor> = {
  Cirurgia:   "red",
  "Pré-op":   "amber",
  Consulta:   "blue",
  "Follow-up":"green",
  Retorno:    "blue",
  Urgência:   "red",
  Outro:      "purple",
};

// ─── Calendar helpers ─────────────────────────────────────────────────────────

export function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function endOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

export function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1);
}

export function endOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0, 23, 59, 59, 999);
}

export function isSameDay(a: Date | number, b: Date | number): boolean {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
}

export function getMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  // Monday-based week (0=Mon … 6=Sun)
  let startOffset = first.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const days: Date[] = [];
  for (let i = startOffset; i > 0; i--) {
    days.push(new Date(year, month, 1 - i));
  }
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d));
  }
  while (days.length % 7 !== 0) {
    const last = days[days.length - 1];
    days.push(new Date(last.getTime() + 86400000));
  }
  return days;
}

export function getWeekDays(refDate: Date): Date[] {
  const day = refDate.getDay(); // 0=Sun
  const mondayOffset = day === 0 ? -6 : 1 - day;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(refDate);
    d.setDate(refDate.getDate() + mondayOffset + i);
    d.setHours(0, 0, 0, 0);
    return d;
  });
}

export function getTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 7; h <= 20; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
  }
  return slots;
}

export function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

export function formatDateShort(ts: number): string {
  return new Date(ts).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

export function durationLabel(startMs: number, endMs: number): string {
  const diff = (endMs - startMs) / 60000;
  if (diff < 60) return `${diff}min`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m > 0 ? `${h}h${m}min` : `${h}h`;
}

// ─── Firestore CRUD ───────────────────────────────────────────────────────────

const agendaCol = (uid: string) => collection(db, "users", uid, "agenda");

export async function createAppointment(uid: string, data: AppointmentInput): Promise<string> {
  const ref = await addDoc(agendaCol(uid), {
    ...data,
    doctorUid: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateAppointment(
  uid: string,
  id: string,
  data: Partial<AppointmentInput>
): Promise<void> {
  await updateDoc(doc(agendaCol(uid), id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteAppointment(uid: string, id: string): Promise<void> {
  await deleteDoc(doc(agendaCol(uid), id));
}

/** Real-time listener for a date range (returns unsubscribe fn) */
export function subscribeToAppointments(
  uid: string,
  rangeStart: number,
  rangeEnd: number,
  onChange: (appointments: Appointment[]) => void
): () => void {
  const q = query(
    agendaCol(uid),
    where("startDate", ">=", rangeStart),
    where("startDate", "<=", rangeEnd),
    orderBy("startDate", "asc")
  );

  return onSnapshot(q, (snap) => {
    const items: Appointment[] = snap.docs.map((d) => {
      const data = d.data();
      const isGoogleEvent = data.origin === "google" || Boolean(data.googleEventId);
      const cleanedNotes = isGoogleEvent
        ? getAppointmentDisplayNotes(data as Partial<Appointment>)
        : String(data.notes ?? data.description ?? "");
      return {
        id: d.id,
        ...(data as Omit<Appointment, "id">),
        notes: cleanedNotes,
        color: normalizeEventColor(data.color),
        startDate: data.startDate instanceof Timestamp ? data.startDate.toMillis() : Number(data.startDate),
        endDate: data.endDate instanceof Timestamp ? data.endDate.toMillis() : Number(data.endDate),
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toMillis() : Number(data.createdAt ?? 0),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toMillis() : Number(data.updatedAt ?? 0),
      };
    });
    onChange(items);
  });
}

/** Store Google OAuth tokens for a user */
export async function saveGoogleCalendarToken(
  uid: string,
  tokenData: { accessToken: string; refreshToken?: string; email?: string; expiresAt?: number }
): Promise<void> {
  await updateDoc(doc(db, "users", uid), {
    "integrations.google_calendar": {
      ...tokenData,
      connectedAt: serverTimestamp(),
    },
  });
}

export async function removeGoogleCalendarToken(uid: string): Promise<void> {
  await updateDoc(doc(db, "users", uid), {
    "integrations.google_calendar": null,
  });
}
